import React from "react";

/**
 * One agent, for someone with about twenty seconds.
 *
 * The call button sits in the header, not the footer. Everything else on the
 * card is there to answer "what am I about to ring?" and nothing more; the
 * detail lives behind a disclosure so the grid stays scannable.
 *
 * Purely presentational. The gallery owns the call, because the Mesa widget is
 * a page-level singleton and only one agent may hold the microphone at a time.
 */

const PHASE_LABEL = {
  permission: "Waiting for your microphone",
  connecting: "Connecting",
  live: "Live",
  ended: "Call ended",
  error: "Problem",
};

function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

const AgentCard = ({ agent, live, active, call, onOpen, onClose }) => {
  const phase = active && call ? call.phase : "ready";
  const inProgress = phase === "permission" || phase === "connecting";

  return (
    <article className={`agent-card${active ? " agent-card--active" : ""}`}>
      <div className="agent-card-head">
        <div className="agent-card-ident">
          <p className="agent-card-kicker">{agent.kicker}</p>
          <h3 className="agent-card-title">{agent.name}</h3>
        </div>

        {live && !active ? (
          <button
            type="button"
            className="button agent-call-button"
            onClick={() => onOpen(agent.id)}
          >
            <i className="fa fa-microphone" aria-hidden="true" /> Talk to it
          </button>
        ) : null}
      </div>

      <p className="agent-card-line">{agent.oneLiner}</p>

      {/* The live panel replaces the flow chain while a call is up, so the
          card never grows taller than it started. */}
      {active ? (
        <div className="agent-panel">
          <div className="agent-panel-head">
            <span className={`agent-phase agent-phase--${phase}`} role="status">
              <span className="agent-phase-dot" aria-hidden="true" />
              {PHASE_LABEL[phase] || "Ready"}
              {inProgress ? "…" : null}
            </span>
            {phase === "live" ? (
              <span className="agent-timer">{formatDuration(call.seconds)}</span>
            ) : null}
          </div>

          {/* The microphone-denied path resets to `ready` and leaves the reason
              here, so this is not only an error state. */}
          {call.problem ? (
            <p className="agent-panel-problem">{call.problem}</p>
          ) : null}

          <div className="agent-panel-actions">
            {phase === "live" ? (
              <button
                type="button"
                className="button agent-hangup-button"
                onClick={call.end}
              >
                Hang up
              </button>
            ) : null}
            {phase === "ready" || phase === "ended" || phase === "error" ? (
              <button
                type="button"
                className="button agent-call-button"
                onClick={() => call.start(agent)}
              >
                {phase === "ready" ? "Call" : "Call again"}
              </button>
            ) : null}
            <button
              type="button"
              className="button agent-close-button"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      ) : (
        <>
          {Array.isArray(agent.flow) && agent.flow.length > 0 ? (
            <ol className="agent-flow" aria-label={`${agent.name} call flow`}>
              {agent.flow.map((step) => (
                <li className="agent-flow-step" key={step}>
                  {step}
                </li>
              ))}
            </ol>
          ) : null}

          <div className="agent-card-foot">
            {agent.outcome ? (
              <p className="agent-outcome">
                <strong>{agent.outcome.value}</strong> {agent.outcome.label}
              </p>
            ) : null}

            {/* No token means no call button and no note about it. Explaining
                an absent feature just draws attention to the gap; the card
                stands on its own as a description of the agent. */}
            {live && agent.tryIt ? (
              <p className="agent-try-it">Try: “{agent.tryIt}”</p>
            ) : null}
          </div>

          {/* Depth on demand. A hiring manager reading the grid should not have
              to scroll past four sets of guard rails to reach the fourth
              agent. */}
          {Array.isArray(agent.guards) && agent.guards.length > 0 ? (
            <details className="agent-details">
              <summary>How it is built</summary>
              <ul className="agent-guard-list">
                {agent.guards.map((guard) => (
                  <li key={guard}>{guard}</li>
                ))}
              </ul>
              {Array.isArray(agent.tools) && agent.tools.length > 0 ? (
                <div className="project-pill-row agent-tools">
                  {agent.tools.map((tool) => (
                    <span className="project-pill" key={tool}>
                      {tool}
                    </span>
                  ))}
                </div>
              ) : null}
            </details>
          ) : null}
        </>
      )}
    </article>
  );
};

export default AgentCard;
